/**
 * Beacon a client-side booking failure the server can't otherwise see — a
 * fetch/parse error AFTER the request left the browser, where the server may
 * actually have succeeded (Defect C: "customer saw an error but it came
 * through"). Best-effort and non-throwing: detection must never break booking.
 */

export type ClientErrorContact = { name?: string; phone?: string; email?: string }

export type ClientErrorReport = {
  reason: string
  submissionId?: string
  path: string
  at: number
  contact?: ClientErrorContact
}

/** Pure, bounded report builder — testable without a DOM. */
export function buildClientErrorReport(
  reason: string,
  submissionId: string | null | undefined,
  path: string,
  at: number,
  contact?: ClientErrorContact,
): ClientErrorReport {
  const report: ClientErrorReport = {
    reason: String(reason).slice(0, 64),
    path: String(path).slice(0, 256),
    at,
  }
  const sid = (submissionId ?? '').slice(0, 80)
  if (sid) report.submissionId = sid
  if (contact) {
    const pick = (v: unknown) =>
      typeof v === 'string' && v.trim() ? v.trim().slice(0, 200) : undefined
    const bounded: ClientErrorContact = {
      name: pick(contact.name),
      phone: pick(contact.phone),
      email: pick(contact.email),
    }
    if (bounded.name || bounded.phone || bounded.email) report.contact = bounded
  }
  return report
}

/** Fire-and-forget beacon. Uses sendBeacon so it survives the modal closing. */
export function reportClientBookError(
  reason: string,
  submissionId: string | null,
  contact?: ClientErrorContact,
): void {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return
  try {
    const report = buildClientErrorReport(
      reason,
      submissionId,
      window.location.pathname,
      Date.now(),
      contact,
    )
    const body = JSON.stringify(report)
    const url = '/api/book/client-error'
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      return
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    })
  } catch {
    // Swallow — a detection beacon must never surface to the customer.
  }
}
